// src/pages/LoginPage.jsx
// Single login for all roles. Logo image centered above the form, theme toggle
// tucked top-right, mobile-first layout.

import React, { useState } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { Mail, Lock, ArrowRight, Receipt, Scan, BarChart3, Ticket, ClipboardList, Link2, Building2 } from "../components/icons";
import PasswordEye from "../components/PasswordEye";
import { useAuth } from "../context/AuthContext";
import { homePathForRole, ENQUIRY_URL } from "../config";
import ThemeToggle from "../components/ThemeToggle";

// ambient floating glyphs behind the card - brand-green, low opacity
const FLOATERS = [
  { Icon: Receipt, top: "12%", left: "12%", size: 30, dur: "7s", delay: "0s" },
  { Icon: Scan, top: "20%", right: "14%", size: 26, dur: "8s", delay: "1s" },
  { Icon: BarChart3, bottom: "16%", left: "16%", size: 28, dur: "9s", delay: "2s" },
  { Icon: Ticket, bottom: "24%", right: "15%", size: 24, dur: "7.5s", delay: "1.5s" },
  { Icon: ClipboardList, top: "42%", left: "7%", size: 22, dur: "8.5s", delay: "0.5s" },
  { Icon: Link2, top: "55%", right: "9%", size: 22, dur: "6.5s", delay: "2.5s" },
  { Icon: Building2, top: "9%", right: "33%", size: 20, dur: "9.5s", delay: "3s" },
  { Icon: Lock, bottom: "11%", right: "38%", size: 20, dur: "8s", delay: "1.2s" },
];

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // already signed in -> go home
  if (user) return <Navigate to={homePathForRole(user.role)} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      const profile = await login(email.trim().toLowerCase(), password);
      const dest = location.state?.from?.pathname || homePathForRole(profile.role);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.message || "Could not sign in.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative overflow-hidden min-h-screen bg-tb-bg dark:bg-tb-night text-tb-ink dark:text-tb-d-text flex flex-col">
      {/* ambient background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-tb-brand/10 blur-3xl" />
        <div className="absolute -bottom-24 -right-20 w-96 h-96 rounded-full bg-tb-brand/10 blur-3xl" />
        {FLOATERS.map(({ Icon, size, top, bottom, left, right, dur, delay }, i) => (
          <div
            key={i}
            className="absolute tb-float text-tb-brand opacity-[0.16] dark:opacity-[0.14]"
            style={{ top, bottom, left, right, animationDuration: dur, animationDelay: delay }}
          >
            <Icon size={size} />
          </div>
        ))}
      </div>

      {/* theme toggle - top right */}
      <div className="relative z-10 flex justify-end px-5 pt-4">
        <ThemeToggle />
      </div>

      {/* centered column */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-5 pb-12">
        {/* logo */}
                <img
          src={process.env.PUBLIC_URL + "/logo-light.png"}
          alt="taxpack"
          className="h-12 sm:h-14 w-auto mb-8 dark:hidden"
        />
        <img
          src={process.env.PUBLIC_URL + "/logo-dark.png"}
          alt="taxpack"
          className="h-12 sm:h-14 w-auto mb-8 hidden dark:block"
        />

        {/* heading */}
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-center">Sign in</h1>
        <p className="mt-1.5 mb-6 text-sm text-tb-muted text-center">
          Access your taxpack portal.
        </p>

        {/* form card */}
        <form
          onSubmit={onSubmit}
          className="w-full max-w-sm surface rounded-2xl border border-tb-border dark:border-tb-d-border p-6 shadow-sm"
        >
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-3 py-2.5 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {/* email */}
          <label className="block text-sm font-medium mb-1.5" htmlFor="email">Email</label>
          <div className="relative mb-4">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-tb-muted" />
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-tb-border dark:border-tb-d-border bg-white dark:bg-tb-d-inset
                         pl-9 pr-3 py-2.5 text-sm outline-none focus:border-tb-brand focus:ring-1 focus:ring-tb-brand
                         placeholder:text-tb-muted/60"
              placeholder="you@company.co.za"
            />
          </div>

          {/* password */}
          <label className="block text-sm font-medium mb-1.5" htmlFor="password">Password</label>
          <div className="relative mb-2">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-tb-muted" />
            <input
              id="password"
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-tb-border dark:border-tb-d-border bg-white dark:bg-tb-d-inset
                         pl-9 pr-12 py-2.5 text-sm outline-none focus:border-tb-brand focus:ring-1 focus:ring-tb-brand
                         placeholder:text-tb-muted/60"
              placeholder="********"
            />
            <PasswordEye visible={showPw} onToggle={() => setShowPw((v) => !v)} />
          </div>

          <p className="mb-5 text-right text-xs text-tb-muted">
            Forgot your password? Reset it in the app
          </p>

          {/* submit */}
          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-tb-cta
                       text-white text-sm font-semibold py-2.5 shadow-green-glow
                       hover:brightness-105 active:scale-[0.98] disabled:opacity-60 transition"
          >
                        {busy ? "Signing in..." : "Sign in"}
            {!busy && <ArrowRight size={16} />}
          </button>

          <p className="mt-5 text-center text-sm text-tb-muted">
            No account yet?{" "}
            <Link to="/register" className="text-tb-brand font-semibold hover:underline">Create one</Link>
          </p>
        </form>

       
      </main>
    </div>
  );
}